import styled from "@emotion/styled";
import { LoginOption, LoginOptionDetails } from "./LoginOption";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Checkbox, Typography } from "@kaltura/ds-react-components";
import React, { useMemo, useRef } from "react";
import { BodyText, InnerContainer, Title, Wrapper } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";

export interface LoginSelectionProps {
    welcomeText: string;
    selectText: string;
    authMethods: LoginOptionDetails[];
    nonce: string;
    referrer?: string;
    submitUrl: string;
    showRememberSelection?: boolean;
    analyticsButtonName?: string;
}

const StyledInnerContainer = styled(InnerContainer)({
    width: "100%",
});

const StyledBodyText = styled(BodyText)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

const Label = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(0, 1),
    cursor: "pointer",
}));

/**
 * Login (auth-method) selection screen
 */
export function LoginSelection({
    welcomeText,
    selectText,
    authMethods,
    referrer = "",
    nonce,
    submitUrl,
    showRememberSelection = true,
    analyticsButtonName,
}: LoginSelectionProps) {
    const formRef = useRef<HTMLFormElement>(null);
    const selectionRef = useRef<HTMLInputElement>(null);

    const sendButtonAnalytics = useButtonAnalytics();

    // used to detect if a tooltip placeholder should be added for options that do not have helpText
    // true only when at least one helpText (tooltip) is available
    const shouldAddHelpTextPlaceholder = useMemo(() => {
        return authMethods.some((authMethod) => (authMethod.helpText || "") !== "");
    }, [authMethods]);

    /**
     * put value in form element and submit form
     * @param selectedOptionValue
     */
    const optionSelectedHandler = (selectedOptionValue: string) => {
        // set selected value in form
        if (selectionRef.current) {
            selectionRef.current.value = selectedOptionValue;
        }
        // send analytics
        analyticsButtonName && sendButtonAnalytics(analyticsButtonName, ButtonClickAnalyticsType.LOGIN);
        // submit form
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    return (
        <Wrapper className="kms-ds-login-selection">
            <StyledInnerContainer>
                <Title variant={"h2"} component={"h1"} align={"center"}>
                    {HtmlReactParser(translate(welcomeText))}
                </Title>
                <StyledBodyText variant={"body1"} align={"center"}>
                    {HtmlReactParser(translate(selectText))}
                </StyledBodyText>
                <div className={"kms-ds-login-selection-options"}>
                    {authMethods.map((authMethod) => (
                        <LoginOption
                            key={authMethod.name}
                            details={authMethod}
                            onClick={(selectedOptionValue: string) => optionSelectedHandler(selectedOptionValue)}
                            shouldAddHelpTextPlaceholder={shouldAddHelpTextPlaceholder}
                        />
                    ))}
                </div>
                <form ref={formRef} action={submitUrl} method={"post"} className={"kms-ds-login-selection-form"}>
                    <input type={"hidden"} name={"nonce"} value={nonce} id={"nonce"} />
                    <input
                        ref={selectionRef}
                        type={"hidden"}
                        name={"LoginSelection[selection]"}
                        id={"LoginSelection-selection"}
                    />
                    <div>
                        {showRememberSelection && (
                            <>
                                <Checkbox
                                    name={"LoginSelection[remember][]"}
                                    id={"LoginSelection-remember-remember"}
                                    defaultChecked={false}
                                    value={"remember"}
                                />
                                <label htmlFor={"LoginSelection-remember-remember"}>
                                    <Label variant={"body2"} component={"span"}>
                                        {translate("Remember my selection")}
                                    </Label>
                                </label>
                            </>
                        )}
                    </div>
                    <input type={"hidden"} name={"referrer"} value={referrer} id={"referrer"} />
                    <input type={"hidden"} name={"ref"} value={referrer} id={"ref"} />
                </form>
            </StyledInnerContainer>
        </Wrapper>
    );
}

export default LoginSelection;
